import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { RemoveTrackerButton } from '../../../components/common/buttons/RemoveTrackerButton.tsx'
import { TrackerAssignmentButton } from '../../../components/common/buttons/TrackerAssignmentButton.tsx'
import { petFromDevice, readableSeconds, renewsOn } from '../../../helpers/dataHelpers.ts'
import controlStore from '../../../store/controlStore.ts'
import dataStore from '../../../store/dataStore.ts'
import { ISubscription } from '../../../interfaces/types.ts'
import { UpgradeSubscriptionButton } from '../../../components/common/buttons/UpgradelSubscriptionButton.tsx'
import { ly } from '../../../helpers/petHelper.tsx'
import { ProvisionDeviceButton } from '../../../components/common/buttons/ProvisionDeviceButton.tsx'
import { isAdvancedSettingsEnabled } from '../../../helpers/sleepHelper.ts'
import { AdvancedSettings } from '../../../components/common/AdvancedSettings/AdvancedSettings.tsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGear } from '@fortawesome/free-solid-svg-icons/faGear'
import { faLocationDot } from '@fortawesome/free-solid-svg-icons/faLocationDot'


export const TrackerSubscriptionList: React.FC = () => {
    const subscriptions = dataStore(s => s.devicePlans)
    const devices = dataStore(s => s.devices)
    const navigate = useNavigate()
    const isMobile = controlStore(s => s.isOnMobileDevice)
    const [activeSubscriptions, setActiveSubscriptions] = React.useState<ISubscription[]>([])
    const [advancedOpenId, setAdvancedOpenId] = React.useState<string | null>(null)
    const advancedEnabled = isAdvancedSettingsEnabled()
    const timezones = dataStore(s => s.timeZones)
    const tzId = dataStore.getState().account.timeZone
    const timeZone = timezones.find(t => t._id === tzId)

    React.useEffect(() => {
        if (subscriptions.length) {
            const activeDevices = devices?.map(d => d.iccid) || []
            const tmp = subscriptions.filter((a: ISubscription) => activeDevices.includes(a.deviceId))
            setActiveSubscriptions(tmp)
        } else {
            setActiveSubscriptions([])
        }
    }, [subscriptions, devices])

    const emptyState = (
        <div className={'flex flex-col items-center gap-4 py-12 text-gray-400'}>
            <FontAwesomeIcon icon={faLocationDot} size={'3x'} />
            <div className={'text-lg'}>No trackers yet</div>
            <div className={'text-sm'}>Add a tracker to get started</div>
        </div>
    )

    const trackerCards = devices?.map(c => {
        const pet = petFromDevice(c)
        const sub = activeSubscriptions.find(s => s.deviceId === c.iccid)
        const planType = sub ? sub.planType.includes('ly') ? sub.planType : ly(sub.planType) : ''

        const isActive = c.isActive
        const cardBorder = isActive ? '' : 'border border-amber-500/40'

        return (
            <div key={c._id}
                 className={`bg-primary/10 rounded-lg ${cardBorder} ${isMobile ? 'p-3' : 'p-5'} flex flex-col gap-3 overflow-hidden`}>

                {/* Name row */}
                <div className={'flex items-center justify-between gap-2'}>
                    <div className={'flex items-center gap-2'}>
                        <div className={'text-xl font-bold text-primary'}>{c.deviceName}</div>
                        {!isActive && (
                            <span className={'text-xs font-medium bg-amber-500/10 text-amber-600 rounded-full px-2 py-0.5'}>
                                Not Activated
                            </span>
                        )}
                    </div>
                    <div className={'text-base-content font-medium text-right'}>
                        {pet ? (
                            <span>
                                <span className={'text-neutral-content/50'}>Assigned to: </span>
                                <span className={'font-semibold'}>{pet.petName}</span>
                            </span>
                        ) : (
                            <span className={'font-semibold text-neutral-content/50'}>Unassigned</span>
                        )}
                    </div>
                </div>

                {/* Detail panel */}
                <div className={'flex justify-between w-full rounded-lg bg-base-100 p-3'}>
                    <div className={'flex flex-col text-left text-base-content font-medium gap-1'}>
                        <div className={'text-neutral-content/50 text-xs uppercase tracking-wide'}>ID</div>
                        <div className={'font-semibold pl-2 mb-2'}>{c.iccid}</div>

                        <div className={'text-neutral-content/50 text-xs uppercase tracking-wide'}>Check-in rate</div>
                        <div className={'font-semibold pl-2'}>{readableSeconds(c.status?.backgroundInterval)}</div>
                    </div>

                    {sub && (
                        <div className={'flex flex-col text-left text-base-content font-medium gap-1'}>
                            <div className={'text-neutral-content/50 text-xs uppercase tracking-wide'}>
                                {planType} Service Plan
                            </div>
                            <div className={'font-semibold pl-2 mb-2'}>${sub.planAmount}</div>

                            <div className={'text-neutral-content/50 text-xs uppercase tracking-wide'}>Renews next</div>
                            <div className={'font-semibold pl-2'}>{renewsOn(timeZone, sub.planStart, sub.planType)}</div>
                        </div>
                    )}
                </div>

                {/* Action buttons */}
                <div className={'flex items-center justify-around w-full pt-1'}>
                    <TrackerAssignmentButton tracker={c} unassign/>
                    {!isActive && <ProvisionDeviceButton tracker={c}/>}
                    {sub?.planType === 'month' ? (
                        <div className={!isActive ? 'opacity-50 pointer-events-none' : ''}>
                            <UpgradeSubscriptionButton subscription={sub}/>
                        </div>
                    ) : null}
                    <RemoveTrackerButton tracker={c}/>
                    {advancedEnabled && (
                        <button
                            className={`btn btn-ghost btn-sm opacity-50 ${!isActive ? 'pointer-events-none' : ''}`}
                            onClick={() => setAdvancedOpenId(c._id)}
                            disabled={!isActive}
                        >
                            <FontAwesomeIcon icon={faGear} />
                        </button>
                    )}
                </div>

                {advancedEnabled && advancedOpenId === c._id && (
                    <AdvancedSettings
                        tracker={c}
                        open={true}
                        setOpen={(o) => !o && setAdvancedOpenId(null)}
                        disabled={!!c.status.deviceCommandInProgressStatus || !c.status.powerOn || c.status.sleeping}
                    />
                )}
            </div>
        )
    })

    return (
        <div className={`flex flex-col gap-6 ${isMobile ? 'w-full' : 'w-[38rem]'} mx-auto pb-6 px-3 max-h-[90vh] overflow-y-auto`}>
            {/* Header */}
            <div className={'sticky top-0 z-10 flex flex-col bg-base-100 pt-6 pb-2 gap-2'}>
                <div className={'flex items-center justify-between'}>
                    <h1 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-primary m-0`}>Gibi Trackers</h1>
                    <div className={'flex items-center gap-2'}>
                        <button className={'btn btn-secondary btn-sm gap-2'} onClick={() => navigate('/addTracker')}>
                            <FontAwesomeIcon icon={faLocationDot} />
                            {isMobile ? 'Add' : 'Add Tracker'}
                        </button>
                        <button className={'btn btn-ghost btn-sm text-primary'} onClick={() => navigate('/' + controlStore.getState().currentPage)}>
                            Close
                        </button>
                    </div>
                </div>
                <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-500 m-0 text-left`}>Manage your trackers and subscriptions</p>
            </div>

            {/* Tracker cards */}
            {devices?.length ? (
                <div className={'flex flex-col gap-3'}>
                    {trackerCards}
                </div>
            ) : emptyState}
        </div>
    )
}
